import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import PageSkeleton from "./skeletons/PageSkeleton";

/**
 * Role-based Protected Route Component
 * Restricts access to routes based on user roles
 *
 * @param {React.ReactNode} children - The component to render if access is granted
 * @param {string|string[]} allowedRoles - Single role or array of roles that can access this route
 * @param {string} redirectTo - Path to redirect to if access is denied (default: '/projects')
 */
const RoleProtectedRoute = ({
  children,
  allowedRoles,
  redirectTo = "/projects",
}) => {
  const { user, isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageSkeleton />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if user has required role
  if (!user) {
    return <Navigate to={redirectTo} replace />;
  }

  // Normalize allowedRoles to array
  const allowedRolesArray = Array.isArray(allowedRoles)
    ? allowedRoles
    : [allowedRoles];

  // Get user's role (check role_name from roles table first, fallback to role for backward compatibility)
  const userRole = user.role_name || user.role;

  // Normalize role names for comparison (handle variations like 'chief-admin', 'chief_admin', 'Chief Admin')
  const normalizedUserRole = userRole
    ? userRole.toLowerCase().replace(/[_\s-]/g, "-")
    : null;
  const normalizedAllowedRoles = allowedRolesArray.map((role) =>
    role.toLowerCase().replace(/[_\s-]/g, "-"),
  );

  // Check if user has one of the allowed roles
  const hasAccess =
    normalizedUserRole && normalizedAllowedRoles.includes(normalizedUserRole);

  if (!hasAccess) {
    // Access denied - redirect to default page
    console.warn(
      `Access denied: User role '${userRole}' is not in allowed roles: ${allowedRolesArray.join(", ")}`,
    );
    return <Navigate to={redirectTo} replace />;
  }

  // Access granted - render the protected component
  return children;
};

export default RoleProtectedRoute;
